import { Spinner } from 'construct-ui';
import moment from 'moment';
import SelectCodeMasterCategoryComponent from '../../../../components/code-master/select-code-master-category.component';
import DatePickerComponent from '../../../../components/date-picker/date-picker.component';
import paginationComponent from '../../../../components/pagination/pagination.component';
import { codeMasterType, formats } from '../../../../config/constants';
import DailyBatchCheckModel from '../../../../models/batch-job-mgmt/daily-batch-check/daily-batch-check.model';
import ViewBase from '../../../view-base.view';

/**
 * Daily Batch Check
 * 
 * A page to check Batch Status
 * 
 * @author cmcnkhiep
 */

export default function DailyBatchCheckView() {
  ViewBase.call(this);

  const DESCENDING_TEXT = 'DESC';
  const ASCENDING_TEXT = 'ASC';
  const DEFAULT_PAGINATION = {
    pageIndex: 1,
    pageSize: 10,
    totalCount: 0
  };
  const DEFAULT_SEARCH_FIELDS = {
    startDate: moment().format(formats.dateFormat),
    endDate: moment().format(formats.dateFormat),
    subsidiary: '',
    spName: '',
    status: ''

  };

  let tableData = [];
  let isLoadingTable = false;
  let selectedSortBy = '';
  let selectedOrderBy = '';

  let searchFields = structuredClone(DEFAULT_SEARCH_FIELDS)
  let currentRequestParams = structuredClone(DEFAULT_SEARCH_FIELDS);

  let pagination = structuredClone(DEFAULT_PAGINATION);

  const flipOrderText = (orderText) => {
    return orderText.toUpperCase() === DESCENDING_TEXT ? ASCENDING_TEXT : DESCENDING_TEXT;
  }

  const onSortClick = (orderBy) => {
    if (selectedOrderBy === orderBy) {
      selectedSortBy = flipOrderText(selectedSortBy);
    } else {
      selectedOrderBy = orderBy;
      selectedSortBy = DESCENDING_TEXT;
    }
    if (!tableData.length) {
      return
    }
    getTableData();
  };

  const getSortIcon = (orderBy) => {
    if (selectedOrderBy !== orderBy) {
      return null;
    }

    return selectedSortBy === DESCENDING_TEXT
      ? m('i.bi.bi-sort-down', { "style": "float:left" })
      : m('i.bi.bi-sort-up', { "style": "float:left" });
  };

  let onPageSizeChanged = (pageIndex, pageSize) => {
    pagination.pageIndex = pageIndex;
    pagination.pageSize = pageSize;
    getTableData();
  }

  let onPageChanged = (pageIndex) => {
    pagination.pageIndex = pageIndex;
    getTableData();
  }

  const requestParams = {
    get pageIndex() {
      return pagination.pageIndex;
    },

    get pageSize() {
      return pagination.pageSize;
    },

    get orderBy() {
      return selectedOrderBy;
    },

    get sortBy() {
      return selectedSortBy;
    },

    get startDate() {
      return moment(currentRequestParams.startDate).format(formats.apiDateFormat);
    },

    get endDate() {
      return moment(currentRequestParams.endDate).format(formats.apiDateFormat);
    },

    get subsidiary() {
      return currentRequestParams.subsidiary;
    },

    get spName() {
      return currentRequestParams.spName.trim();
    },

    get status() {
      if (currentRequestParams.status === "DIDN’T RUN") return "DIDN";
      return currentRequestParams.status;
    },

  }

  function resetSearchFields() {
    searchFields = structuredClone(DEFAULT_SEARCH_FIELDS)
  }

  function updateSearchFields(attr, value) {
    if (Array.isArray(searchFields[attr])) {
      searchFields[attr].splice(0, Infinity, ...value);
    } else {
      searchFields[attr] = value;
    }
  }

  let getTableData = () => {
    isLoadingTable = true;
    currentRequestParams.isExcelDownload = false;
    DailyBatchCheckModel.searchDailyBatchCheck(requestParams)
      .then((result) => {
        if (!result) {
          tableData = [];
          return;
        }

        pagination.totalCount = result.total;
        tableData = result.data;
      })
      .catch((error) => {
        tableData = [];
        this.handleError(error);
      })
      .finally(() => {
        isLoadingTable = false;
      })
  }

  let search = (e) => {
    // Form validation
    if (!this.validateForm(e)) {
      return false;
    }

    pagination.pageIndex = DEFAULT_PAGINATION.pageIndex;
    currentRequestParams = structuredClone(searchFields)
    getTableData()

    return false;
  }

  let clearFilters = (e) => {
    e.preventDefault();
    let form = document.querySelector('.needs-validation');
    form.classList.remove('was-validated');
    resetSearchFields();
  }

  return {
    view: (vnode) => {
      return [
        // Search section
        m('.d-flex.flex-column.align-items-end.gap-2.mb-3',
          m('#search-section.card.w-100.mb-0',
            m('.card-body',
              m('form.row.g-4.align-items-end.needs-validation[novalidate]',
                {
                  onsubmit: search
                },
                m('.col-xl-3.col-lg-4.col-sm-6.position-relative',
                  m('label.form-label[for="dateRangeInput"]', this.getText('LT00000070', 'Date Range')),
                  m(DatePickerComponent, {
                    id: 'dateRangeInput',
                    class: 'form-control',
                    placeholder: this.getText('LT00000070', 'Date Range'),
                    isRange: true,
                    dateStart: searchFields.startDate,
                    dateEnd: searchFields.endDate,
                    onselect: (e) => {
                      updateSearchFields('startDate', e.detail.start);
                      updateSearchFields('endDate', e.detail.end);
                    }
                  })
                ),
                m('.col-xl-3.col-lg-4.col-sm-6',
                  m('label.form-label[for=subsidiary]', this.getText('LT00000576', 'Subsidiary')),
                  m(SelectCodeMasterCategoryComponent, {
                    id: 'subsidiary',
                    category: codeMasterType.batchJobSubsidiary,
                    isMultiple: false,
                    keyOnly: true,
                    selectedItem: searchFields.subsidiary,
                    onSelect: (value) => {
                      updateSearchFields('subsidiary', value);
                    }
                  })
                ),
                m('.col-xl-3.col-lg-4.col-sm-6.position-relative',
                  m('label.form-label[for="spName"]', this.getText("LT00001122", "Stored Procedure")),
                  m('input#spName.form-control[name="spName"]', {
                    placeholder: this.getText("LT00001122", "Stored Procedure"),
                    maxLength: 100,
                    value: searchFields.spName,
                    onchange: (e) => {
                      updateSearchFields('spName', e.target.value)
                    }
                  })
                ),
                m('.col-xl-3.col-lg-4.col-sm-6',
                  m('label.form-label[for=status]', this.getText('LT00000015', 'Status')),
                  m(SelectCodeMasterCategoryComponent, {
                    id: 'status',
                    category: codeMasterType.batchJobStatus,
                    isMultiple: false,
                    keyOnly: true,
                    selectedItem: searchFields.status,
                    onSelect: (value) => {
                      updateSearchFields('status', value);
                    }
                  })
                ),

                // Search & clear buttons
                m('.col-12.d-flex.justify-content-end.gap-2',
                  m('button.btn.btn-primary[type="submit"]', {
                  }, this.getText("LT00000001", "Search")),
                  m('button.btn.btn-white', {
                    onclick: clearFilters
                  }, this.getText("LT00000031", "Clear")),
                ),
              ),
            )
          ),
        ),
        // Search section - end

        // Table section
        m('.card',
          m('.card-body',
            m('.row.g-2 ',
              m('.col-12.p-0',
                m('.table-responsive',
                  m('table.table', [
                    m('thead.thead-bggrey',
                      m('tr.align-middle',
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('subsidiary'); } },
                          getSortIcon('subsidiary'),
                          this.getText("LT00000576", "Subsidiary")
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('storedProcedure'); } },
                          getSortIcon('storedProcedure'),
                          this.getText("LT00001122", "Stored Procedure")
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('type'); } },
                          getSortIcon('type'),
                          this.getText("LT00000117", "Type")
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('frequency'); } },
                          getSortIcon('frequency'),
                          this.getText("LT00000353", "Frequency")
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('status'); } },
                          getSortIcon('status'),
                          this.getText("LT00000015", "Status")
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          {
                            onclick: () => { onSortClick('remarks'); }
                          },
                          getSortIcon('remarks'),
                          this.getText("LT00000152", "Remarks")
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('duration'); } },
                          getSortIcon('duration'),
                          this.getText("LT00001123", "Duration (s)")
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('rowCount'); } },
                          getSortIcon('rowCount'),
                          this.getText("LT00001124", "Row Count")
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('startDate'); } },
                          getSortIcon('startDate'),
                          this.getText("LT00000301", "Start Date")
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('endDate'); } },
                          getSortIcon('endDate'),
                          this.getText("LT00000302", "End Date")
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('location'); } },
                          getSortIcon('location'),
                          this.getText("LT00000557", "Location")
                        ),
                      )
                    ),
                    m('tbody',
                      m(Spinner, {
                        active: isLoadingTable,
                        background: true,
                        fill: true,
                        size: 'xl',
                      }),
                      (tableData.length == 0) &&
                      m('tr.align-middle',
                        m('td[colspan=22]', this.getText("EM00000141", "No records found."))
                      ),
                      tableData.length > 0 &&
                      tableData.map((col, index) => [
                        m('tr.align-middle',
                          m('td', col.subsidiary),
                          m('td', col.storedProcedure),
                          m('td', col.type),
                          m('td', col.frequency),
                          m('td', col.status),
                          m('td.text-wrap', col.remarks),
                          m('td', col.duration),
                          m('td', col.rowCount),
                          m('td', col.startDate),
                          m('td', col.endDate),
                          m('td', col.location),

                        )
                      ])
                    )
                  ])
                ),
                tableData.length > 0 &&
                m(".px-3",
                  m(paginationComponent, {
                    currentPage: pagination.pageIndex,
                    pageSize: pagination.pageSize,
                    totalItems: pagination.totalCount,
                    onPageChanged: onPageChanged,
                    onPageSizeChanged: onPageSizeChanged
                  })
                )
              )
            )
          )
        ),
        // Table section - end

        this.getBaseViews()
      ]
    }
  }
}